import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../../api/axios';
import Breadcrumb from '../../components/ui/Breadcrumb';

const BudgetCreate = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const caseFileIdParam = searchParams.get('case_file_id');

  const [docCategories, setDocCategories] = useState([]);
  const [docTypes, setDocTypes] = useState([]);
  const [caseFiles, setCaseFiles] = useState([]);
  const [notarySettings, setNotarySettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    budget_document_type_id: '',
    case_file_id: caseFileIdParam || '',
    client_name: '',
    client_phone: '',
    client_email: '',
    client_address: '',
    responsible_name: '',
    language: 'es',
    currency: 'mxn',
    show_bank_accounts: true,
    notes: '',
  });

  const [isPublic, setIsPublic] = useState(!caseFileIdParam);
  const [selectedCaseFile, setSelectedCaseFile] = useState(null);
  const [selectedDocType, setSelectedDocType] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get('/budget-document-categories'),
      api.get('/budget-document-types'),
      api.get('/case-files', { params: { per_page: 100 } }),
      api.get('/notary-settings'),
    ]).then(([catsRes, typesRes, cfRes, notaryRes]) => {
      setDocCategories(catsRes.data);
      setDocTypes(typesRes.data);
      setCaseFiles(cfRes.data.data || cfRes.data);
      setNotarySettings(notaryRes.data);

      // Si viene con case_file_id pre-seleccionado
      if (caseFileIdParam) {
        const cf = (cfRes.data.data || cfRes.data).find(c => c.id === parseInt(caseFileIdParam));
        if (cf) {
          setSelectedCaseFile(cf);
          setForm(prev => ({
            ...prev,
            responsible_name: cf.assigned_user?.name || '',
          }));
        }
      }
    }).finally(() => setLoading(false));
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));

    if (name === 'budget_document_type_id') {
      const dt = docTypes.find(t => t.id === parseInt(value));
      setSelectedDocType(dt || null);
      if (dt) {
        setForm(prev => ({ ...prev, show_bank_accounts: dt.show_bank_accounts }));
      }
    }

    if (name === 'case_file_id') {
      const cf = caseFiles.find(c => c.id === parseInt(value));
      setSelectedCaseFile(cf || null);
      if (cf) {
        setForm(prev => ({ ...prev, responsible_name: cf.assigned_user?.name || '' }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.budget_document_type_id) {
      toast.error('Selecciona un tipo de documento');
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (isPublic) {
        payload.case_file_id = null;
      }
      const res = await api.post('/budgets', payload);
      toast.success('Presupuesto creado');
      navigate(`/budgets/${res.data.id}`);
    } catch (error) {
      toast.error('Error al crear presupuesto');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="text-center py-5"><div className="spinner-border text-primary"></div></div>;

  const typesByCategory = docCategories.map(cat => ({
    ...cat,
    types: docTypes.filter(t => t.budget_document_category_id === cat.id),
  })).filter(cat => cat.types.length > 0);

  return (
    <div className="container-fluid">
      <Breadcrumb items={[{ label: 'Presupuestos', to: '/budgets' }, { label: 'Nuevo Presupuesto' }]} />
      <div className="page-header d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0">Nuevo Presupuesto</h4>
        <Link to="/budgets" className="btn btn-outline-secondary btn-sm">
          <i className="ti-arrow-left mr-1"></i> Regresar
        </Link>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="row">
          <div className="col-12 col-md-8">

            {/* Tipo de documento */}
            <div className="card mb-4">
              <div className="card-header"><h6 className="mb-0">Tipo de Documento</h6></div>
              <div className="card-body">
                <div className="form-group">
                  <label>Tipo de Documento <span className="text-danger">*</span></label>
                  <select name="budget_document_type_id" className="form-control"
                    value={form.budget_document_type_id} onChange={handleChange} required>
                    <option value="">Seleccionar...</option>
                    {typesByCategory.map(cat => (
                      <optgroup key={cat.id} label={cat.name}>
                        {cat.types.map(type => (
                          <option key={type.id} value={type.id}>
                            {type.series ? `[${type.series}] ` : ''}{type.name}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                </div>

                {selectedDocType && (
                  <div className="alert alert-info py-2 mb-0">
                    <small>
                      <strong>Serie:</strong> {selectedDocType.series || '—'} &nbsp;·&nbsp;
                      <strong>Siguiente folio:</strong> {selectedDocType.series ? `${selectedDocType.series}-${selectedDocType.folio_sequence}` : selectedDocType.folio_sequence} &nbsp;·&nbsp;
                      <strong>Conceptos:</strong> {selectedDocType.concept_categories?.reduce((acc, cat) => acc + cat.concepts?.length, 0) || 0}
                    </small>
                  </div>
                )}
              </div>
            </div>

            {/* Cliente */}
            <div className="card mb-4">
              <div className="card-header"><h6 className="mb-0">Cliente</h6></div>
              <div className="card-body">
                {/* Toggle público/expediente */}
                <div className="d-flex mb-3" style={{ gap: '8px' }}>
                  <button type="button"
                    className={`btn btn-sm ${!isPublic ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => { setIsPublic(false); }}>
                    Vincular a Expediente
                  </button>
                  <button type="button"
                    className={`btn btn-sm ${isPublic ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => { setIsPublic(true); setForm(prev => ({ ...prev, case_file_id: '' })); setSelectedCaseFile(null); }}>
                    Público en General
                  </button>
                </div>

                {!isPublic ? (
                  <div className="form-group">
                    <label>Expediente</label>
                    <select name="case_file_id" className="form-control"
                      value={form.case_file_id} onChange={handleChange}>
                      <option value="">Seleccionar expediente...</option>
                      {caseFiles.map(cf => (
                        <option key={cf.id} value={cf.id}>
                          {cf.client?.name} — {cf.title}
                        </option>
                      ))}
                    </select>
                    {selectedCaseFile && (
                      <div className="mt-2 p-2 border rounded bg-light">
                        <small>
                          <strong>{selectedCaseFile.client?.name}</strong>
                          {selectedCaseFile.client?.phone && ` · ${selectedCaseFile.client.phone}`}
                          {selectedCaseFile.client?.email && ` · ${selectedCaseFile.client.email}`}
                        </small>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="row">
                    <div className="col-12 col-md-6">
                      <div className="form-group">
                        <label>Nombre del cliente</label>
                        <input type="text" name="client_name" className="form-control"
                          placeholder="PÚBLICO EN GENERAL"
                          value={form.client_name} onChange={handleChange} />
                      </div>
                    </div>
                    <div className="col-12 col-md-6">
                      <div className="form-group">
                        <label>Teléfono</label>
                        <input type="text" name="client_phone" className="form-control"
                          value={form.client_phone} onChange={handleChange} />
                      </div>
                    </div>
                    <div className="col-12 col-md-6">
                      <div className="form-group">
                        <label>Correo</label>
                        <input type="email" name="client_email" className="form-control"
                          value={form.client_email} onChange={handleChange} />
                      </div>
                    </div>
                    <div className="col-12 col-md-6">
                      <div className="form-group">
                        <label>Dirección</label>
                        <input type="text" name="client_address" className="form-control"
                          value={form.client_address} onChange={handleChange} />
                      </div>
                    </div>
                  </div>
                )}

                <div className="form-group mb-0">
                  <label>Abogado / Responsable</label>
                  <input type="text" name="responsible_name" className="form-control"
                    placeholder={notarySettings?.notary_name || ''}
                    value={form.responsible_name} onChange={handleChange} />
                </div>
              </div>
            </div>

            {/* Notas */}
            <div className="card mb-4">
              <div className="card-header"><h6 className="mb-0">Notas</h6></div>
              <div className="card-body">
                <textarea name="notes" className="form-control" rows="3"
                  placeholder="Observaciones adicionales para el presupuesto..."
                  value={form.notes} onChange={handleChange}></textarea>
              </div>
            </div>
          </div>

          {/* Panel derecho — opciones */}
          <div className="col-12 col-md-4">
            <div className="card mb-4">
              <div className="card-header"><h6 className="mb-0">Configuración</h6></div>
              <div className="card-body">
                <div className="form-group">
                  <label>Idioma</label>
                  <select name="language" className="form-control" value={form.language} onChange={handleChange}>
                    <option value="es">Español</option>
                    <option value="en">English</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Moneda</label>
                  <select name="currency" className="form-control" value={form.currency} onChange={handleChange}>
                    <option value="mxn">Pesos MXN</option>
                    <option value="usd">Dólares USD</option>
                  </select>
                </div>
                {form.currency === 'usd' && (
                  <div className="alert alert-info py-2">
                    <small>
                      Tipo de cambio actual: <strong>${notarySettings?.usd_exchange_rate} MXN/USD</strong>
                      <br />Puedes ajustarlo en el detalle del presupuesto.
                    </small>
                  </div>
                )}
                <div className="custom-control custom-checkbox">
                  <input type="checkbox" className="custom-control-input" id="showBankAccounts"
                    name="show_bank_accounts"
                    checked={form.show_bank_accounts}
                    onChange={handleChange} />
                  <label className="custom-control-label" htmlFor="showBankAccounts">
                    Mostrar cuentas bancarias en PDF
                  </label>
                </div>
              </div>
            </div>

            {selectedDocType?.concept_categories?.length > 0 && (
              <div className="card mb-4">
                <div className="card-header"><h6 className="mb-0">Conceptos que se cargarán</h6></div>
                <div className="card-body p-0">
                  {selectedDocType.concept_categories.map(cat => (
                    <div key={cat.id}>
                      <div className="px-3 py-2 border-bottom" style={{ backgroundColor: '#f8f9fa' }}>
                        <small className="font-weight-bold">{cat.name}</small>
                      </div>
                      {cat.concepts?.map(concept => (
                        <div key={concept.id} className="d-flex justify-content-between px-3 py-1 border-bottom">
                          <small className="text-muted">{concept.name}</small>
                          <small>${Number(concept.default_amount).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</small>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="d-flex flex-wrap" style={{ gap: '10px' }}>
              <button type="submit" className="btn btn-primary btn-block" disabled={submitting}>
                {submitting ? 'Creando...' : 'Crear Presupuesto'}
              </button>
              <Link to="/budgets" className="btn btn-outline-secondary btn-block">Cancelar</Link>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default BudgetCreate;
